import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '../components/shared/cp-page-subnav.js';

const SYSTEM_TABS = [
  { label: 'Settings', path: '/settings' },
  { label: 'Agenten', path: '/agents' },
  { label: 'Team & Skills', path: '/team' },
  { label: 'Agent-Skills', path: '/skills' },
  { label: 'Routinen', path: '/cron' },
  { label: 'Monitoring', path: '/monitoring' },
];

type SkillScope = 'private' | 'company' | 'global';

interface AgentSkill {
  id: number;
  name: string;
  description: string;
  full_markdown: string;
  scope: SkillScope;
  created_by_agent_id: number | null;
  company_id: number | null;
  approved_at: number | null;
  creator_agent_name?: string;
  created_at: number;
  updated_at: number;
}

type FilterScope = 'all' | SkillScope;
type ViewState = 'loading' | 'loaded' | 'empty' | 'error';

@customElement('cp-page-skills')
export class CpPageSkills extends LitElement {
  createRenderRoot() { return this; }

  @state() private _skills: AgentSkill[] = [];
  @state() private _viewState: ViewState = 'loading';
  @state() private _error = '';
  @state() private _filterScope: FilterScope = 'all';
  @state() private _selectedSkill: AgentSkill | null = null;
  @state() private _promoting = false;
  @state() private _promoteError = '';
  @state() private _deleting = false;

  connectedCallback(): void {
    super.connectedCallback();
    void this._load();
  }

  private async _load(): Promise<void> {
    this._viewState = 'loading';
    try {
      const res = await fetch('/api/agent-skills');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as AgentSkill[];
      this._skills = data;
      this._viewState = data.length === 0 ? 'empty' : 'loaded';
      // Refresh selected skill if still in list
      if (this._selectedSkill) {
        const refreshed = data.find(s => s.id === this._selectedSkill!.id);
        this._selectedSkill = refreshed ?? null;
      }
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Ladefehler';
      this._viewState = 'error';
    }
  }

  private get _filteredSkills(): AgentSkill[] {
    if (this._filterScope === 'all') return this._skills;
    return this._skills.filter(s => s.scope === this._filterScope);
  }

  private _selectSkill(skill: AgentSkill): void {
    this._selectedSkill = skill;
    this._promoteError = '';
  }

  private async _promote(scope: SkillScope, companyId?: number): Promise<void> {
    if (!this._selectedSkill) return;
    this._promoting = true;
    this._promoteError = '';
    try {
      const body: Record<string, unknown> = { scope };
      if (companyId !== undefined) body['company_id'] = companyId;
      const res = await fetch(`/api/agent-skills/${this._selectedSkill.id}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        this._promoteError = data.error ?? 'Fehler beim Promoten';
        return;
      }
      await this._load();
    } catch (e) {
      this._promoteError = e instanceof Error ? e.message : 'Netzwerkfehler';
    } finally {
      this._promoting = false;
    }
  }

  private async _demote(): Promise<void> {
    if (!this._selectedSkill) return;
    this._promoting = true;
    this._promoteError = '';
    try {
      const res = await fetch(`/api/agent-skills/${this._selectedSkill.id}/demote`, { method: 'POST' });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        this._promoteError = data.error ?? 'Fehler beim Demoten';
        return;
      }
      await this._load();
    } catch (e) {
      this._promoteError = e instanceof Error ? e.message : 'Netzwerkfehler';
    } finally {
      this._promoting = false;
    }
  }

  private async _delete(): Promise<void> {
    if (!this._selectedSkill) return;
    if (!confirm(`Skill "${this._selectedSkill.name}" wirklich löschen?`)) return;
    this._deleting = true;
    try {
      // Founder-context: route detects founder via session, no agent_id needed.
      const res = await fetch(`/api/agent-skills/${this._selectedSkill.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const data = await res.json() as { error?: string };
        this._promoteError = data.error ?? 'Fehler beim Löschen';
        return;
      }
      this._selectedSkill = null;
      await this._load();
    } catch (e) {
      this._promoteError = e instanceof Error ? e.message : 'Netzwerkfehler';
    } finally {
      this._deleting = false;
    }
  }

  private _renderScopeBadge(scope: SkillScope) {
    const classes: Record<SkillScope, string> = {
      private: 'cp-badge cp-badge--neutral',
      company: 'cp-badge cp-badge--warning',
      global: 'cp-badge cp-badge--success',
    };
    const labels: Record<SkillScope, string> = {
      private: 'Privat',
      company: 'Company',
      global: 'Global',
    };
    return html`<span class="${classes[scope]}">${labels[scope]}</span>`;
  }

  private _renderSkillList() {
    const skills = this._filteredSkills;
    if (skills.length === 0) {
      return html`<div class="cp-empty-state"><p>Keine Skills für diesen Filter.</p></div>`;
    }
    return html`
      <div class="cp-skill-list">
        ${skills.map(skill => html`
          <div
            class="cp-skill-card ${this._selectedSkill?.id === skill.id ? 'cp-skill-card--selected' : ''}"
            @click=${() => this._selectSkill(skill)}
            role="button"
            tabindex="0"
          >
            <div class="cp-skill-card__header">
              <span class="cp-skill-card__name">${skill.name}</span>
              ${this._renderScopeBadge(skill.scope)}
            </div>
            <p class="cp-skill-card__description">${skill.description}</p>
            ${skill.creator_agent_name ? html`
              <span class="cp-skill-card__meta">Erstellt von: ${skill.creator_agent_name}</span>
            ` : nothing}
          </div>
        `)}
      </div>
    `;
  }

  private _renderDetail() {
    const skill = this._selectedSkill;
    if (!skill) {
      return html`<div class="cp-skill-detail cp-skill-detail--empty"><p>Skill auswählen</p></div>`;
    }

    return html`
      <div class="cp-skill-detail">
        <div class="cp-skill-detail__header">
          <h2 class="cp-skill-detail__name">${skill.name}</h2>
          ${this._renderScopeBadge(skill.scope)}
        </div>

        <p class="cp-skill-detail__description">${skill.description}</p>

        ${skill.creator_agent_name ? html`
          <p class="cp-skill-detail__meta">Erstellt von Agent: ${skill.creator_agent_name}</p>
        ` : nothing}

        <pre class="cp-skill-detail__markdown">${skill.full_markdown}</pre>

        ${this._promoteError ? html`
          <div class="cp-alert cp-alert--error">${this._promoteError}</div>
        ` : nothing}

        <div class="cp-skill-detail__actions">
          ${skill.scope === 'private' ? html`
            <button
              class="cp-btn cp-btn--secondary"
              @click=${() => this._promote('company', skill.company_id ?? undefined)}
              ?disabled=${this._promoting}
            >
              Companyweit verfügbar machen
            </button>
            <button
              class="cp-btn cp-btn--primary"
              @click=${() => this._promote('global')}
              ?disabled=${this._promoting}
            >
              Anwendungsweit verfügbar machen
            </button>
          ` : nothing}

          ${skill.scope === 'company' ? html`
            <button
              class="cp-btn cp-btn--primary"
              @click=${() => this._promote('global')}
              ?disabled=${this._promoting}
            >
              Anwendungsweit verfügbar machen
            </button>
            <button
              class="cp-btn cp-btn--ghost"
              @click=${() => this._demote()}
              ?disabled=${this._promoting}
            >
              Zurücksetzen auf Privat
            </button>
          ` : nothing}

          ${skill.scope === 'global' ? html`
            <button
              class="cp-btn cp-btn--ghost"
              @click=${() => this._demote()}
              ?disabled=${this._promoting}
            >
              Zurücksetzen auf Privat
            </button>
          ` : nothing}

          <button
            class="cp-btn cp-btn--danger"
            @click=${() => this._delete()}
            ?disabled=${this._deleting}
          >
            Skill löschen
          </button>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <cp-page-subnav .tabs=${SYSTEM_TABS} activePath="/skills"></cp-page-subnav>

      <div class="cp-page-skills">
        <div class="cp-page-skills__toolbar">
          <h1 class="cp-page-skills__title">Agent-Skills</h1>
          <div class="cp-page-skills__filters">
            ${(['all', 'private', 'company', 'global'] as const).map(scope => html`
              <button
                class="cp-btn cp-btn--sm ${this._filterScope === scope ? 'cp-btn--active' : 'cp-btn--ghost'}"
                @click=${() => { this._filterScope = scope; }}
              >
                ${scope === 'all' ? 'Alle' : scope.charAt(0).toUpperCase() + scope.slice(1)}
              </button>
            `)}
          </div>
        </div>

        ${this._viewState === 'loading' ? html`<div class="cp-loading">Lade Skills…</div>` : nothing}
        ${this._viewState === 'error' ? html`<div class="cp-alert cp-alert--error">${this._error}</div>` : nothing}

        ${this._viewState !== 'loading' ? html`
          <div class="cp-page-skills__layout">
            <div class="cp-page-skills__list">
              ${this._renderSkillList()}
            </div>
            <div class="cp-page-skills__detail">
              ${this._renderDetail()}
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-skills': CpPageSkills;
  }
}
